import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Copy, Check, ExternalLink, RefreshCw, AlertTriangle, CheckCircle2, Clock, Link2, Search, Merge } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Panel monitor · Webhooks FareHarbor
 * Ruta: /app/asistente/webhooks-fh
 *
 * Muestra los últimos eventos recibidos con estado de matching. Fase 1 sólo
 * observación — no modifica reservas. En Fase 2 añadiremos "Auto-apply".
 */
export default function FareHarborWebhooksPage() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState(null);
  const [copied, setCopied] = useState(false);
  const [assignFor, setAssignFor] = useState(null); // evento para asignación manual

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["fh-webhooks"],
    queryFn: async () => (await api.get("/webhooks/fareharbor/admin/events?limit=100")).data,
    refetchInterval: 15_000,
  });

  const detailQ = useQuery({
    queryKey: ["fh-webhook-detail", selected?.uuid],
    queryFn: async () => (await api.get(`/webhooks/fareharbor/admin/events/${selected.uuid}`)).data,
    enabled: !!selected?.uuid,
  });

  const items = data?.items || [];
  const total = data?.total || 0;
  const linked = items.filter((e) => e.match_status === "linked").length;
  const pending = items.filter((e) => e.match_status === "pending_manual_match").length;
  const rejected = items.filter((e) => e.status === "rejected_bad_secret").length;
  // Reenvíos ahorrados: FareHarbor reenvía cada booking varias veces (reconciliación).
  // Los eventos con `resent_count > 0` son resends idénticos que ya no ocupan una
  // fila propia — se registran como "×N" en la fila del evento original.
  const resendsSaved = items.reduce((s, e) => s + (e.resent_count || 0), 0);

  const webhookUrl = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/webhooks/fareharbor/{SECRET}`;

  const copyUrl = () => {
    navigator.clipboard.writeText(webhookUrl);
    setCopied(true);
    toast.success("URL de webhook copiada");
    setTimeout(() => setCopied(false), 2000);
  };

  const [dedupeStats, setDedupeStats] = useState(null);
  const [dedupeLoading, setDedupeLoading] = useState(false);

  const runDedupePreview = async () => {
    setDedupeLoading(true);
    try {
      const res = await api.post("/webhooks/fareharbor/admin/dedupe-resends?dry_run=true");
      setDedupeStats(res.data);
    } catch (e) {
      toast.error("Error consultando duplicados: " + (e?.response?.data?.detail || e.message));
    } finally {
      setDedupeLoading(false);
    }
  };

  const runDedupeApply = async () => {
    setDedupeLoading(true);
    try {
      const res = await api.post("/webhooks/fareharbor/admin/dedupe-resends?dry_run=false");
      const stats = res.data;
      setDedupeStats(stats);
      toast.success(
        `Consolidados ${stats.groups_consolidated} grupo(s) · ${stats.duplicates_deleted} evento(s) duplicados eliminados.`,
      );
      qc.invalidateQueries({ queryKey: ["fh-webhooks"] });
    } catch (e) {
      toast.error("Error aplicando dedup: " + (e?.response?.data?.detail || e.message));
    } finally {
      setDedupeLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-4 p-4" data-testid="fh-webhooks-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Webhooks FareHarbor</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Monitor de eventos recibidos desde FareHarbor. Fase 1: sólo observación (no toca reservas).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                onClick={runDedupePreview}
                disabled={dedupeLoading}
                data-testid="fh-dedupe-btn"
                title="Consolida eventos históricos duplicados (mismo booking + mismo contenido)."
              >
                {dedupeLoading ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Merge className="mr-1.5 h-3.5 w-3.5" />}
                Consolidar duplicados
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="fh-dedupe-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Consolidar duplicados históricos</AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-2 text-sm">
                    <p>
                      FareHarbor reenvía cada booking varias veces (reconciliación).
                      Los reenvíos idénticos <b>no aportan información nueva</b> y
                      pueden consolidarse: se conserva el evento original y se
                      cuenta cuántos reenvíos hubo.
                    </p>
                    {dedupeStats ? (
                      <div className="rounded border bg-slate-50 p-2.5 font-mono text-xs">
                        <div>Eventos totales: <b>{dedupeStats.total_events_before}</b></div>
                        <div>Grupos con duplicados: <b>{dedupeStats.groups_consolidated}</b></div>
                        <div className="text-red-700">Duplicados que se borrarían: <b>{dedupeStats.duplicates_deleted}</b></div>
                        <div>Eventos tras dedupe: <b>{dedupeStats.total_events_after}</b></div>
                      </div>
                    ) : (
                      <div className="text-xs text-muted-foreground">Calculando estadísticas…</div>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Los eventos únicos (con cambios reales de status/precio/hora)
                      se preservan intactos.
                    </p>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="fh-dedupe-cancel">Cancelar</AlertDialogCancel>
                <AlertDialogAction
                  onClick={runDedupeApply}
                  disabled={dedupeLoading || !dedupeStats || dedupeStats.duplicates_deleted === 0}
                  data-testid="fh-dedupe-confirm"
                >
                  Consolidar {dedupeStats?.duplicates_deleted || 0}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Button onClick={() => refetch()} variant="outline" size="sm" disabled={isFetching} data-testid="fh-refresh">
            {isFetching ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-1.5 h-3.5 w-3.5" />}
            Actualizar
          </Button>
        </div>
      </div>

      {/* Métricas */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <MetricCard label="Total recibidos" value={total} color="slate" />
        <MetricCard label="Linked automático" value={linked} color="emerald" icon={<CheckCircle2 className="h-4 w-4" />} />
        <MetricCard label="Pending manual" value={pending} color="amber" icon={<Clock className="h-4 w-4" />} />
        <MetricCard label="Rechazados (bad secret)" value={rejected} color="red" icon={<AlertTriangle className="h-4 w-4" />} />
        <MetricCard
          label="Reenvíos ahorrados"
          value={resendsSaved}
          color="slate"
          icon={<Merge className="h-4 w-4" />}
        />
      </div>

      {/* URL setup */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            URL destino a enviar a FareHarbor Support
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <code className="flex-1 rounded border bg-slate-50 px-3 py-2 text-xs text-slate-800">
              {webhookUrl}
            </code>
            <Button size="sm" variant="outline" onClick={copyUrl} data-testid="fh-copy-url">
              {copied ? <Check className="mr-1.5 h-3.5 w-3.5" /> : <Copy className="mr-1.5 h-3.5 w-3.5" />}
              {copied ? "Copiado" : "Copiar"}
            </Button>
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">
            Reemplaza <code>{"{SECRET}"}</code> por el valor de <code>FAREHARBOR_WEBHOOK_SECRET</code> en el .env del backend.
          </p>
        </CardContent>
      </Card>

      {/* Tabla eventos */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-semibold">Últimos eventos ({items.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center p-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              Aún no hay eventos recibidos. Cuando Romain active el webhook en FareHarbor, empezarán a aparecer aquí en tiempo real.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-left uppercase tracking-wider text-[10px] text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2">Recibido</th>
                    <th className="px-3 py-2">Proveedor</th>
                    <th className="px-3 py-2">Actividad</th>
                    <th className="px-3 py-2">Cliente</th>
                    <th className="px-3 py-2">Fecha</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Match</th>
                    <th className="px-3 py-2 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((e, i) => (
                    <tr
                      key={`${e.uuid || e.received_at}-${i}`}
                      className="cursor-pointer border-t hover:bg-slate-50"
                      onClick={() => setSelected(e)}
                      data-testid={`fh-row-${i}`}
                    >
                      <td className="px-3 py-2 font-mono text-[11px] text-muted-foreground">
                        <div className="flex items-center gap-1.5">
                          <span>{fmtDate(e.received_at)}</span>
                          {e.resent_count > 0 && (
                            <span
                              title={e.last_received_at ? `Último resend: ${fmtDate(e.last_received_at)}` : "Reenvíos idénticos deduplicados"}
                              className="rounded-full bg-slate-200/70 px-1.5 py-[1px] text-[9px] font-semibold text-slate-600"
                              data-testid={`fh-resent-badge-${i}`}
                            >
                              ×{e.resent_count + 1}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2">{e.company_name || e.company_shortname || "—"}</td>
                      <td className="px-3 py-2">{e.item_name || "—"}</td>
                      <td className="px-3 py-2">{e.contact_name || "—"}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-[11px]">
                        {fmtDate(e.start_at, true)}
                      </td>
                      <td className="px-3 py-2">
                        <StatusBadge status={e.status} />
                      </td>
                      <td className="px-3 py-2">
                        <MatchBadge status={e.match_status} />
                      </td>
                      <td className="px-3 py-2 text-right font-mono">
                        {e.receipt_total != null ? `${(e.receipt_total / 100).toFixed(2)} €` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail modal */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-h-[85vh] max-w-4xl overflow-y-auto" data-testid="fh-detail-modal">
          <DialogHeader>
            <DialogTitle className="text-sm">
              Evento webhook · {selected?.uuid || selected?.received_at}
            </DialogTitle>
          </DialogHeader>
          {detailQ.isLoading && <Loader2 className="mx-auto h-6 w-6 animate-spin" />}
          {detailQ.data && (
            <div className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Proveedor" value={detailQ.data.company_name} />
                <Field label="Actividad" value={detailQ.data.item_name} />
                <Field label="UUID" value={detailQ.data.uuid} mono />
                <Field label="PK / Display ID" value={`${detailQ.data.pk} · ${detailQ.data.display_id || "—"}`} mono />
                <Field label="Voucher" value={detailQ.data.voucher_number} />
                <Field label="Status" value={detailQ.data.status} />
                <Field label="Cliente" value={`${detailQ.data.contact_name || "—"} · ${detailQ.data.contact_email || ""}`} />
                <Field label="Fecha actividad" value={fmtDate(detailQ.data.start_at, true)} />
                <Field label="Pickup" value={detailQ.data.pickup_name} />
                <Field label="Total booking" value={detailQ.data.receipt_total != null ? `${(detailQ.data.receipt_total / 100).toFixed(2)} €` : "—"} />
              </div>
              {detailQ.data.confirmation_url && (
                <a
                  href={detailQ.data.confirmation_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-emerald-700 hover:underline"
                >
                  <ExternalLink className="h-3.5 w-3.5" /> Ver voucher FareHarbor
                </a>
              )}
              {/* Estado matching y acciones */}
              <div className="flex flex-wrap items-center gap-2 rounded border bg-slate-50 p-3">
                <MatchBadge status={detailQ.data.match_status} />
                {detailQ.data.match_level && (
                  <Badge variant="outline">nivel {detailQ.data.match_level}</Badge>
                )}
                {detailQ.data.matched_reserva_id && (
                  <span className="text-[11px] text-muted-foreground">
                    Reserva #{detailQ.data.matched_reserva_id.slice(0, 8)}…
                  </span>
                )}
                <div className="ml-auto flex gap-2">
                  <Button
                    size="sm" variant="outline"
                    onClick={async () => {
                      try {
                        await api.post(`/webhooks/fareharbor/admin/events/${detailQ.data.uuid}/rematch`);
                        toast.success("Rematching lanzado");
                        qc.invalidateQueries({ queryKey: ["fh-webhooks"] });
                        qc.invalidateQueries({ queryKey: ["fh-webhook-detail", detailQ.data.uuid] });
                      } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
                    }}
                    data-testid="fh-rematch"
                  >
                    <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Reintentar match
                  </Button>
                  {detailQ.data.match_status !== "linked" && (
                    <Button
                      size="sm"
                      onClick={() => setAssignFor(detailQ.data)}
                      data-testid="fh-assign-open"
                    >
                      <Link2 className="mr-1.5 h-3.5 w-3.5" /> Asignar manualmente
                    </Button>
                  )}
                </div>
              </div>
              <details className="rounded border bg-slate-50 p-3">
                <summary className="cursor-pointer text-xs font-semibold text-muted-foreground">Payload raw JSON</summary>
                <pre className="mt-2 max-h-96 overflow-auto text-[10px] leading-tight">
                  {JSON.stringify(detailQ.data.payload_raw, null, 2)}
                </pre>
              </details>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal asignación manual */}
      <AssignReservaDialog
        event={assignFor}
        onClose={() => setAssignFor(null)}
        onDone={() => {
          setAssignFor(null);
          qc.invalidateQueries({ queryKey: ["fh-webhooks"] });
          qc.invalidateQueries({ queryKey: ["fh-webhook-detail"] });
        }}
      />
    </div>
  );
}

function AssignReservaDialog({ event, onClose, onDone }) {
  const [q, setQ] = useState("");
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [busy, setBusy] = useState(null);

  React.useEffect(() => {
    if (event?.contact_name) setQ(event.contact_name);
    else setQ("");
    setSelectedTrip(null);
  }, [event]);

  // Candidatas sugeridas: reservas con mismo contact_name + fecha ±1
  const candidatesQ = useQuery({
    queryKey: ["fh-candidates", event?.uuid],
    queryFn: async () =>
      (await api.get(`/webhooks/fareharbor/admin/events/${event.uuid}/candidates`)).data,
    enabled: !!event?.uuid,
  });

  const tripsQ = useQuery({
    queryKey: ["fh-assign-trips", q],
    queryFn: async () =>
      (await api.get(`/trips`, { params: { q, limit: 15 } })).data,
    enabled: !!event && q.length >= 3,
  });

  const reservasQ = useQuery({
    queryKey: ["fh-assign-reservas", selectedTrip?.id],
    queryFn: async () => (await api.get(`/trips/${selectedTrip.id}`)).data,
    enabled: !!selectedTrip?.id,
  });

  const trips = tripsQ.data?.items || [];
  const reservas = (reservasQ.data?.reservas || []);
  const suggested = candidatesQ.data?.reservas || [];

  const doAssign = async (reservaId) => {
    setBusy(reservaId);
    try {
      await api.post(`/webhooks/fareharbor/admin/events/${event.uuid}/assign`, {
        reserva_id: reservaId,
      });
      toast.success("Reserva vinculada al webhook");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error asignando");
    } finally {
      setBusy(null);
    }
  };

  if (!event) return null;

  return (
    <Dialog open={!!event} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto" data-testid="fh-assign-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm">Asignar webhook a una reserva SOFI</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-xs">
          {/* Datos del webhook */}
          <div className="rounded border bg-emerald-50 p-3">
            <div className="font-semibold text-emerald-900">Webhook FareHarbor:</div>
            <div className="mt-1 grid grid-cols-2 gap-1 text-[11px]">
              <div><b>Cliente:</b> {event.contact_name}</div>
              <div><b>Fecha:</b> {fmtDate(event.start_at, true)}</div>
              <div className="col-span-2"><b>Actividad:</b> {event.item_name}</div>
              <div><b>Proveedor:</b> {event.company_name}</div>
              <div><b>PK:</b> {event.pk}</div>
            </div>
          </div>

          {/* Candidatas sugeridas por cliente+fecha (aunque provider no coincida) */}
          {candidatesQ.isLoading && (
            <div className="rounded border border-dashed p-3 text-center text-muted-foreground">
              Buscando reservas del cliente en fechas cercanas…
            </div>
          )}
          {suggested.length > 0 && (
            <div className="rounded border border-amber-200 bg-amber-50/60 p-2">
              <div className="mb-1 flex items-center gap-1 px-1 text-[10px] font-semibold uppercase tracking-wider text-amber-800">
                <AlertTriangle className="h-3 w-3" />
                Sugerencias · Reservas del cliente en fechas cercanas ({suggested.length})
              </div>
              <div className="mb-2 px-1 text-[10px] text-amber-900/80">
                Aunque el proveedor asignado en SOFI no coincida con el de FareHarbor,
                puede tratarse de la misma actividad (error de captura antiguo).
              </div>
              <div className="max-h-56 space-y-1 overflow-y-auto">
                {suggested.map((r) => {
                  const dupHint = r.provider_duplicate_of ? " (duplicado)" : "";
                  return (
                    <div
                      key={r.id}
                      className="flex items-start justify-between gap-2 rounded border border-amber-200 bg-white px-2 py-2 text-[11px]"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold">
                          {r.trip_main_traveler || "—"}
                          {r.trip_code && <span className="ml-1 text-muted-foreground">· {r.trip_code}</span>}
                        </div>
                        <div className="text-muted-foreground">
                          {r.date_entry} {r.hour_entry ? `· ${r.hour_entry}` : ""} · {r.service || r.type || "reserva"}
                        </div>
                        <div className="text-[10px]">
                          <b>Proveedor SOFI:</b> {r.provider_name || "sin proveedor"}{dupHint}
                          {r.fh_pk && <> · <span className="text-emerald-700">ya linkada fh_pk={r.fh_pk}</span></>}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => doAssign(r.id)}
                        disabled={busy === r.id}
                        data-testid={`fh-suggest-${r.id}`}
                      >
                        {busy === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Vincular"}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Buscador manual de trips */}
          <div>
            <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {suggested.length > 0 ? "O busca otro trip manualmente" : "Busca el trip del cliente"}
            </label>
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Nombre del cliente o email…"
                className="pl-7"
                data-testid="fh-assign-search"
              />
            </div>
            {q.length >= 3 && (
              <div className="mt-2 max-h-40 overflow-y-auto rounded border">
                {tripsQ.isLoading ? (
                  <div className="p-3 text-center text-muted-foreground">Buscando…</div>
                ) : trips.length === 0 ? (
                  <div className="p-3 text-center text-muted-foreground">Sin resultados</div>
                ) : (
                  trips.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => setSelectedTrip(t)}
                      className={`flex w-full items-center justify-between border-b px-3 py-2 text-left text-xs last:border-b-0 hover:bg-slate-50 ${selectedTrip?.id === t.id ? "bg-emerald-50" : ""}`}
                      data-testid={`fh-trip-${t.id}`}
                    >
                      <span>
                        <b>{t.main_traveler || t.title || "—"}</b>
                        <span className="ml-2 text-muted-foreground">
                          {t.start_date} → {t.end_date}
                        </span>
                      </span>
                      {selectedTrip?.id === t.id && <Check className="h-4 w-4 text-emerald-700" />}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
          {/* Reservas del trip seleccionado */}
          {selectedTrip && (
            <div>
              <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                2. Elige la reserva a vincular
              </label>
              {reservasQ.isLoading ? (
                <div className="p-3 text-center text-muted-foreground">Cargando reservas…</div>
              ) : (
                <div className="max-h-64 space-y-1 overflow-y-auto rounded border p-1">
                  {reservas.map((r) => {
                    const dateHit = (r.date_entry === (event.start_at || "").slice(0, 10)) ||
                                    (r.date_exit === (event.start_at || "").slice(0, 10));
                    return (
                      <div
                        key={r.id}
                        className={`flex items-start justify-between gap-2 rounded px-2 py-2 text-[11px] ${dateHit ? "bg-emerald-50 border border-emerald-200" : "border border-transparent"}`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold">{r.service || r.type || "—"}</div>
                          <div className="text-muted-foreground">
                            {r.date_entry} · {r.provider_name || "sin proveedor"} · {r.type}
                            {r.fh_pk && <> · <span className="text-emerald-700">ya linkada fh_pk={r.fh_pk}</span></>}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => doAssign(r.id)}
                          disabled={busy === r.id}
                          data-testid={`fh-assign-reserva-${r.id}`}
                        >
                          {busy === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Vincular"}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MetricCard({ label, value, color = "slate", icon }) {
  const colorMap = {
    slate: "bg-slate-50 text-slate-900 border-slate-200",
    emerald: "bg-emerald-50 text-emerald-900 border-emerald-200",
    amber: "bg-amber-50 text-amber-900 border-amber-200",
    red: "bg-red-50 text-red-900 border-red-200",
  };
  return (
    <Card className={colorMap[color] || colorMap.slate}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
          {icon}
        </div>
        <div className="mt-1 text-2xl font-semibold">{value}</div>
      </CardContent>
    </Card>
  );
}

function StatusBadge({ status }) {
  const map = {
    booked: "bg-emerald-100 text-emerald-800",
    cancelled: "bg-red-100 text-red-800",
    rebooked: "bg-amber-100 text-amber-800",
    rejected_bad_secret: "bg-red-200 text-red-900",
  };
  return <Badge className={map[status] || "bg-slate-100 text-slate-700"}>{status || "—"}</Badge>;
}

function MatchBadge({ status }) {
  if (status === "linked")
    return <Badge className="bg-emerald-100 text-emerald-800">✓ linked</Badge>;
  if (status === "pending_manual_match")
    return <Badge className="bg-amber-100 text-amber-800">⏳ manual</Badge>;
  return <Badge variant="outline">—</Badge>;
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`mt-0.5 ${mono ? "font-mono text-[11px]" : ""}`}>{value || "—"}</div>
    </div>
  );
}

function fmtDate(v, short = false) {
  if (!v) return "—";
  try {
    const d = new Date(v);
    if (short)
      return d.toLocaleString("es-ES", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
    return d.toLocaleString("es-ES");
  } catch {
    return v;
  }
}
